/**
 * Carburettor icing, the CARBURETOR FREEZES realism toggle. Manual, verbatim:
 * *"At high altitudes, it gets very cold. The carburetors would often freeze,
 * especially during winter months. This would cause a complete loss of power
 * until the carburetor would thaw out, which could only come about if the
 * aircraft was brought to a much lower altitude."*
 *
 * That gives the mechanic and no numbers — flight-tunables.md §8 confirms
 * nothing per-aircraft or global exists in the data — so every threshold and
 * probability here is a CALIBRATION-PENDING tunable. The behaviour that is
 * *not* negotiable is the shape: freeze above one altitude, thaw only below a
 * strictly lower one, after a delay.
 */

import type { RngBank } from "./rng.ts";
import type { Tunables } from "./tunables.ts";

export interface CarburettorState {
  readonly iced: boolean;
  /** Seconds spent continuously below the thaw altitude while iced; resets on any climb back above it. */
  readonly thawSeconds: number;
}

export interface CarburettorUpdate {
  readonly enabled: boolean;
  readonly altitudeFt: number;
  readonly dt: number;
  readonly tunables: Tunables;
  readonly rng: RngBank;
}

export const CLEAR_CARBURETTOR: CarburettorState = { iced: false, thawSeconds: 0 };

export function updateCarburettor(state: CarburettorState, update: CarburettorUpdate): CarburettorState {
  const { tunables } = update;
  if (!update.enabled) return state.iced ? CLEAR_CARBURETTOR : state;

  if (!state.iced) {
    if (update.altitudeFt <= tunables.carbFreezeAltitudeFt) return state;
    const hazard = tunables.carbFreezeProbabilityPerSecond * tunables.carbSeasonFactor * update.dt;
    return update.rng.chance("carbIce", hazard) ? { iced: true, thawSeconds: 0 } : state;
  }

  if (update.altitudeFt >= tunables.carbThawAltitudeFt) {
    return state.thawSeconds === 0 ? state : { iced: true, thawSeconds: 0 };
  }
  const thawSeconds = state.thawSeconds + update.dt;
  return thawSeconds >= tunables.carbThawDelaySeconds ? CLEAR_CARBURETTOR : { iced: true, thawSeconds };
}
